import React from 'react';
import { Icon, Label, Popup } from 'semantic-ui-react';
import type { ContractListDto, LatestDocusignSendSummaryDto } from '../../services/contracts/ContractService';
import { formatCnpj } from '../../services/utils/stringUtils';
import { isMeiOuIndividual } from '../../utils/iePolicy';
import {
  formatDocusignTooltip,
  getDocusignBadgeConfig,
} from '../../utils/docusignSendHistoryUtils';
import { ContractHandsetBadge } from './ContractHandsetBadge';

type ContractListClientCellProps = {
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] };
};

function StaticDocusignBadge({
  contract,
}: {
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] };
}) {
  const badge = getDocusignBadgeConfig(contract);
  const label = (
    <Label
      size="tiny"
      color={badge.color}
      style={{ marginTop: '4px', cursor: 'default' }}
    >
      {badge.text}
    </Label>
  );

  if (badge.showDetailTooltip && badge.detailSummary) {
    return (
      <Popup
        content={<DocusignTooltipContent summary={badge.detailSummary} />}
        trigger={label}
        hoverable
      />
    );
  }

  if (badge.hoverMessage) {
    return (
      <Popup
        content={<div style={{ whiteSpace: 'pre-line' }}>{badge.hoverMessage}</div>}
        trigger={label}
        hoverable
      />
    );
  }

  return label;
}

function DocusignTooltipContent({ summary }: { summary: LatestDocusignSendSummaryDto }) {
  return <div style={{ whiteSpace: 'pre-line' }}>{formatDocusignTooltip(summary)}</div>;
}

export const ContractListClientCell: React.FC<ContractListClientCellProps> = ({ contract }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <strong className="contract-client-name">
        {contract.clientRazaoSocial || 'Cliente não informado'}
        {contract.clientEstado && ` - ${contract.clientEstado}`}
      </strong>
      {contract.token?.trim() && (
        <Label color="brown" size="mini" style={{ cursor: 'default' }}>
          Token: {contract.token.trim()}
        </Label>
      )}
      {contract.fastchip && !contract.fastchipEnviadoEm && (
        <Popup
          content="Contrato Fastchip - aguardando envio do chip"
          trigger={
            <Label color="orange" size="mini" style={{ cursor: 'default' }}>
              <Icon name="bolt" />
              FASTCHIP
            </Label>
          }
        />
      )}
      {contract.fastchip && contract.fastchipEnviadoEm && (
        <Popup
          content={`Fast Chip enviado em ${new Date(contract.fastchipEnviadoEm).toLocaleString('pt-BR')}`}
          trigger={
            <Label color="teal" size="mini" style={{ cursor: 'default' }}>
              <Icon name="shipping fast" />
              FC Enviado
            </Label>
          }
        />
      )}
    </div>
    {(contract.clientNomeFantasia || contract.clientCnpj) && (
      <div className="contract-client-subtitle" style={{ fontSize: '0.9em', color: '#666' }}>
        {contract.clientNomeFantasia && contract.clientNomeFantasia.trim()
          ? `${contract.clientNomeFantasia} - ${formatCnpj(contract.clientCnpj)}`
          : formatCnpj(contract.clientCnpj)}
      </div>
    )}
    {contract.isGroupParent && (
      <Label size="tiny" color="blue" style={{ marginTop: '4px', cursor: 'default' }}>
        <Icon name="folder open" />
        Contratos Agrupados ({contract.groupContracts?.length || 0})
      </Label>
    )}
    {contract.temPortabilidade && (
      <Label size="tiny" color="orange" style={{ marginTop: '4px', cursor: 'default' }}>
        <Icon name="exchange" />
        Portabilidade
      </Label>
    )}
    {contract.temTrocaLinha && (
      <Label size="tiny" color="orange" style={{ marginTop: '4px', cursor: 'default' }}>
        <Icon name="random" />
        Troca de Linha
      </Label>
    )}
    <ContractHandsetBadge contract={contract} interactive={false} />
    {!contract.ieBypassedAtCreation &&
      !isMeiOuIndividual({ naturezaJuridica: contract.clientNaturezaJuridica }) &&
      contract.clientHasActiveInscricaoEstadual === false && (
        <Popup
          content="Cliente sem Inscrição Estadual ativa no UF. Contratos Fastchip são permitidos; o bloqueio de IE inapta aplica-se apenas a entrega TIM (sem Fastchip) com Aditivo."
          trigger={
            <Label size="tiny" color="red" style={{ marginTop: '4px', cursor: 'default' }}>
              <Icon name="warning sign" />
              IE INAPTA
            </Label>
          }
        />
      )}
    {contract.ieBypassedAtCreation && (
      <Popup
        content="Este contrato foi criado com bypass da validação de IE inapta. Risco de recusa pela TIM."
        trigger={
          <Label size="tiny" color="orange" style={{ marginTop: '4px', cursor: 'default' }}>
            <Icon name="warning circle" />
            BYPASS IE
          </Label>
        }
      />
    )}
  {contract.latestDocusignSend ? <StaticDocusignBadge contract={contract} /> : null}
    {contract.trackingCode && (
      <Popup
        content={contract.trackingCode}
        trigger={
          <Label
            size="tiny"
            color="teal"
            style={{
              marginTop: '4px',
              cursor: 'default',
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon name="truck" />
            {contract.trackingCode.length > 12
              ? `${contract.trackingCode.substring(0, 12)}...`
              : contract.trackingCode}
          </Label>
        }
      />
    )}
  </div>
);
